import Swal from "sweetalert2";
import { rootsExistInDataset, getEntriesForRootKeys } from "./dictionary-db";
import {
  stripHarakat,
  normalizeArabicChar,
  normalizeArabicWord,
  foldLetterVariants,
} from "./arabicNormalize";
import { analyzeRoots } from "./rootDeriver";

export function addToastSwipeToDismiss() {
  const popup = Swal.getPopup();
  if (!popup) return;

  let startX = 0, startY = 0, currentX = 0, currentY = 0;
  const THRESHOLD = 60;
  const DRAG_SLOP = 5; // px of movement before a touch counts as a drag, not a tap

  // A drag that starts on the toast belongs to the toast. Otherwise the browser also
  // pans the page underneath, so swiping the toast down scrolled the page.
  popup.style.touchAction = "none";

  popup.addEventListener("touchstart", (e) => {
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    currentX = startX;
    currentY = startY;
    popup.style.transition = "none";
  }, { passive: true });

  popup.addEventListener("touchmove", (e) => {
    currentX = e.touches[0].clientX;
    currentY = e.touches[0].clientY;
    // Non-passive so this can stop the scroll where touch-action isn't honoured.
    if (e.cancelable && Math.hypot(currentX - startX, currentY - startY) > DRAG_SLOP) {
      e.preventDefault();
    }
    const dx = Math.max(0, currentX - startX);
    const dy = Math.max(0, currentY - startY);
    const dominant = dx >= dy ? "x" : "y";
    popup.style.transform = dominant === "x" ? `translateX(${dx}px)` : `translateY(${dy}px)`;
    popup.style.opacity = String(Math.max(0, 1 - Math.max(dx, dy) / 120));
  }, { passive: false });

  popup.addEventListener("touchend", () => {
    const dx = Math.max(0, currentX - startX);
    const dy = Math.max(0, currentY - startY);
    const dist = Math.max(dx, dy);
    const dominant = dx >= dy ? "x" : "y";
    if (dist > THRESHOLD) {
      popup.style.transition = "transform 0.18s ease-out, opacity 0.18s ease-out";
      popup.style.transform = dominant === "x" ? "translateX(200px)" : "translateY(120px)";
      popup.style.opacity = "0";
      setTimeout(() => Swal.close(), 180);
    } else {
      popup.style.transition = "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.2s ease-out";
      popup.style.transform = "translate(0,0)";
      popup.style.opacity = "1";
    }
  }, { passive: true });
}

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
    addToastSwipeToDismiss();
  },
});

export const toastSuccess = (message) => {
  Toast.fire({
    icon: "success",
    title: "Success!",
    text: message,
  });
};

export const toastError = (message) => {
  Toast.fire({
    icon: "error",
    title: "Error!",
    text: message,
  });
};

// A low-key informational tip (e.g. teaching a keyboard shortcut). Longer-lived
// than a success/error toast and closeable.
export const toastTip = (message) => {
  Toast.fire({
    icon: "info",
    title: "Tip",
    text: message,
    timer: 6000,
    showCloseButton: true,
  });
};

// A titled, closeable info toast (e.g. "Dictionary updated").
export const toastNotice = (title, message) => {
  Toast.fire({
    icon: "info",
    titleText: title,
    text: message,
    timer: 6000,
    showCloseButton: true,
  });
};

// For a wait the user has to end (e.g. by closing another tab): no timer. Returns a
// function that closes it, if it's still the toast on screen.
export const toastWaiting = (title, message) => {
  Toast.fire({
    icon: "info",
    titleText: title,
    text: message,
    timer: 0,
    showCloseButton: true,
  });
  const popup = Swal.getPopup();
  return () => {
    if (popup && Swal.getPopup() === popup) Swal.close();
  };
};

// Collections are stored in plain objects keyed by name, so a name that shadows an
// Object.prototype member ("constructor", "hasOwnProperty", "__proto__", …) breaks
// lookups and can blank the Flashcards page. Reject those at the input dialogs.
export const isReservedCollectionName = (name) => name in Object.prototype;

// export const noResultsAlert = (word, callback) => {};

// stripHarakat + normalizeArabicChar now live in ./arabicNormalize (shared with
// the dictionary index so the live query and the stored keys stay consistent).

// Anything that can't be part of a search key: not an Arabic letter, and not a
// Latin letter left over from transliteration (accented ones included, so "café"
// or "ō" misses instead of quietly turning into another root).
const NON_KEY_CHAR = /[^\u0621-\u064A\u0671-\u06D3\p{Script=Latin}]/gu;
const LATIN_LETTERS = /\p{Script=Latin}/gu;

export function processInputToArabic(input) {
  // Presentation forms and Persian/Urdu keyboard letters -> standard letters, then
  // drop any harakat so vocalized Arabic input matches the unvocalized index.
  input = stripHarakat(foldLetterVariants(input));

  const transliterationMap = {
    a: "ا",
    b: "ب",
    t: "ت",
    th: "ث",
    v: "ث",
    j: "ج",
    H: "ح",
    7: "ح",
    kh: "خ",
    x: "خ",
    5: "خ",
    d: "د",
    dh: "ذ",
    r: "ر",
    z: "ز",
    s: "س",
    sh: "ش",
    S: "ص",
    9: "ص",
    D: "ض",
    T: "ط",
    6: "ط",
    Z: "ظ",
    gh: "غ",
    g: "غ",
    3: "ع",
    e: "ع",
    f: "ف",
    q: "ق",
    8: "ق",
    k: "ك",
    l: "ل",
    m: "م",
    n: "ن",
    h: "ه",
    w: "و",
    y: "ي",
    2: "ء",
    // Scholarly transliteration, as Hans Wehr prints it (ḥ, ḵ, š, ‘ for ayn and ’ for
    // hamza), and the other common systems' letters for the same sounds.
    "ḥ": "ح",
    "ḵ": "خ",
    "ḫ": "خ",
    "ẖ": "خ",
    "š": "ش",
    "ṣ": "ص",
    "ḍ": "ض",
    "ṭ": "ط",
    "ẓ": "ظ",
    "ṯ": "ث",
    "ḏ": "ذ",
    "ḳ": "ق",
    "ġ": "غ",
    "ǧ": "ج",
    "ā": "ا",
    "ī": "ي",
    "ū": "و",
    "‘": "ع",
    "ʿ": "ع",
    "ʻ": "ع",
    "’": "ء",
    "ʾ": "ء",
    "ʼ": "ء",
  };

  const convertToken = (token) => {
    let result = "";
    let i = 0;
    while (i < token.length) {
      const rawTwoChar = token.slice(i, i + 2);
      const rawOneChar = token[i];

      const twoChar = transliterationMap[rawTwoChar]
        ? rawTwoChar
        : rawTwoChar.toLowerCase();
      const oneChar = transliterationMap[rawOneChar]
        ? rawOneChar
        : rawOneChar.toLowerCase();

      if (transliterationMap[twoChar]) {
        result += transliterationMap[twoChar];
        i += 2;
      } else if (transliterationMap[oneChar]) {
        result += transliterationMap[oneChar];
        i += 1;
      } else if (oneChar === "i" || oneChar === "u") {
        // Short vowels aren't written in a root: kutub, 3ilm -> كتب, علم.
        i += 1;
      } else {
        result += normalizeArabicChar(rawOneChar);
        i += 1;
      }
    }
    return result;
  };

  const converted = input.split(" ").map(convertToken).join("");

  // Canonical key, the same form the index stores: fold the transliterated letters
  // too ("2" -> ء -> ا, so s2l == sal == سأل) and drop punctuation, digits and
  // emoji. Latin letters the map couldn't convert stay, so the lookup misses rather
  // than matching some other root. Input with no letters at all is returned as is,
  // because callers navigate to /search/<key> and an empty key has no route.
  let key = "";
  for (const ch of converted) key += normalizeArabicChar(ch);
  key = key.replace(NON_KEY_CHAR, "");
  return key || converted;
}

// The key both search bars (Home and the word page) navigate to: letters only. Input
// that is only harakat, tatweel or punctuation gives "", and "%" (history's decodeURI
// throws on it) or "?", "#", "/" (they end the path segment) never reach the route.
// Letters the typing guide can't convert stay, for transliterationHint to name.
export const toSearchKey = (input) => processInputToArabic(input || "").replace(/[^\p{L}]/gu, "");

// The word page's route for a search key.
export const searchPath = (key) => `/search/${encodeURIComponent(key)}`;

// The search keys a list filter's query (History, Bookmarks) stands for. Case picks
// the letter (S is ص, s is س), so the query is converted as typed, and also with its
// first letter lowercased: a phone keyboard capitalizes that unasked unless the field
// opts out, and not every keyboard honours the opt-out ("Slm" still finds سلم).
export const filterQueryKeys = (query) => {
  const typed = (query || "").trim();
  const lowered = typed.charAt(0).toLowerCase() + typed.slice(1);
  return [...new Set([typed, lowered].map(processInputToArabic))].filter(Boolean);
};

// A key that still has Latin letters wasn't fully transliterated (e.g. "book").
export const hasUnconvertedLatin = (key) => /\p{Script=Latin}/u.test(key || "");

// What to tell someone whose input left Latin letters in the key, instead of
// searching for it; null when the key is fully converted.
export const transliterationHint = (key) => {
  const letters = [...new Set((key || "").match(LATIN_LETTERS) || [])];
  if (!letters.length) return null;
  const quoted = letters.map((l) => `"${l}"`).join(", ");
  return `${quoted} ${letters.length > 1 ? "have" : "has"} no Arabic letter in the typing guide. Type the root's consonants (ktb for كتب), or type in Arabic.`;
};

export function stripHTMLTags(str) {
  return str.replace(/<[^>]+>/g, "");
}

// The word as typed, letters only: harakat, tatweel, punctuation, bidi marks and
// spaces go; hamza seats and ى stay, since the analyzer needs them. A pasted «كتاب»,
// or a right-to-left mark in front of the word, used to get no analysis at all.
const NON_LETTER = /[^\u0621-\u064A\u0671-\u06D3]/g;
const typedSpelling = (s) => stripHarakat(foldLetterVariants(s || "")).replace(NON_LETTER, "");

// The spelling root analysis starts from: what was typed, when it has Arabic letters
// (hamza seats intact), or else the search key (Latin input, or a chip, history or
// URL entry).
export const rootAnalysisSpelling = (typed, key) => typedSpelling(typed) || typedSpelling(key);

// aramorph and the dictionary sometimes file a defective root under different final
// weak letters (aramorph gives شفو for مستشفى, which the dictionary lists under
// شفي), so each derived root is also tried with its final و/ي swapped.
const swapFinalWeak = (key) => {
  if (key.endsWith("و")) return key.slice(0, -1) + "ي";
  if (key.endsWith("ي")) return key.slice(0, -1) + "و";
  return null;
};

// The dictionary keys a final weak radical as ي in most roots (رمي) but as ى in a
// few (شوى), and aramorph has its own mix (جىء), so a root is tried both ways.
const withFinalMaqsura = (key) => (key && key.endsWith("ي") ? key.slice(0, -1) + "ى" : null);

// How much a reading adds to its stem, in letters, but counting the article ال as
// one (it is a single, very common clitic: البنك is ال + بنك, not ألّب + نك) and
// not counting a ت that is the word's own ة before a possessive (قوتها = قوة + ها).
const POSSESSIVE_AFTER_TA = /^ت(ه|ها|هم|هما|هن|ك|كم|كما|كن|ي|نا)$/;
const affixWeight = ({ prefix, suffix }) =>
  prefix.length + suffix.length - (prefix.includes("ال") ? 1 : 0) - (POSSESSIVE_AFTER_TA.test(suffix) ? 1 : 0);

// The words the entries list (verb forms and nouns), at three strictnesses of
// spelling: as written (hamza seats and ى kept), with hamza seats folded (the key
// form), and with ى folded into ي as well. Each maps a word to "form" or "noun"; a
// form wins when the same word is both.
const entryWordKinds = (entries) => {
  const tiers = [new Map(), new Map(), new Map()];
  const put = (map, word, kind) => {
    if (word.length >= 2 && map.get(word) !== "form") map.set(word, kind);
  };
  for (const doc of entries) {
    for (const [kind, items] of [["form", doc.forms], ["noun", doc.nouns]]) {
      for (const item of items || []) {
        for (const token of String(item.text || "").split(/[\s,،/]+/)) {
          const folded = normalizeArabicWord(token);
          put(tiers[0], typedSpelling(token), kind);
          put(tiers[1], folded, kind);
          put(tiers[2], folded.replace(/ى/g, "ي"), kind);
        }
      }
    }
  }
  return tiers;
};

// The three spellings of a word, matching entryWordKinds' tiers.
const spellingTiers = (typed) => {
  const folded = normalizeArabicWord(typed);
  return [typed, folded, folded.replace(/ى/g, "ي")];
};

// Ranks the roots the analyzer finds for `input` that exist in the dictionary,
// best first, one per entry. See getRootSuggestionsDetailed for the order.
const rankDerivedRoots = async (input, queryKey) => {
  let analyses = [];
  try {
    analyses = await analyzeRoots(input);
  } catch (e) {
    console.warn("[getRootSuggestions] analyzeRoots failed:", e);
  }

  // Normalize each derived root into searchable-root keys (both spellings of a
  // final weak radical, plus its و/ي twin), drop the query itself, and collect what
  // the ranking needs per key.
  const candidates = new Map();
  for (const a of analyses) {
    const key = processInputToArabic(a.root).replace(/ى/g, "ي");
    const twin = swapFinalWeak(key);
    const affixLength = affixWeight(a);
    const spellings = [[key, false], [withFinalMaqsura(key), false], [twin, true], [withFinalMaqsura(twin), true]];
    for (const [k, guessed] of spellings) {
      if (!k || k === queryKey) continue;
      let c = candidates.get(k);
      if (!c) {
        c = { key: k, order: candidates.size, guessed, affixLength, stems: new Set() };
        candidates.set(k, c);
      }
      c.guessed = c.guessed && guessed;
      c.affixLength = Math.min(c.affixLength, affixLength);
      c.stems.add(a.stem);
      c.stems.add(a.stem + a.suffix);
      // ة is written ت before a suffix (وزارتها = وزار + تها), and gives way to the
      // plural ات (وكالات = وكال + ات): the entry lists وزارة, وكالة.
      if (a.suffix.startsWith("ت") || a.suffix.startsWith("ات")) c.stems.add(a.stem + "ة");
    }
  }

  const entriesByKey = await getEntriesForRootKeys([...candidates.keys()]);
  const word = spellingTiers(input);
  const ranked = [];
  for (const [key, entries] of entriesByKey) {
    const c = candidates.get(key);
    const tiers = entryWordKinds(entries);
    // 3: an entry lists the word exactly as typed, 2: up to hamza seats, 1: up to ى/ي.
    const tier = tiers.findIndex((kinds, i) => kinds.has(word[i]));
    c.wordListed = tier === -1 ? 0 : 3 - tier;
    c.listedAsVerb = tier !== -1 && tiers[tier].get(word[tier]) === "form";
    c.stemListed = [...c.stems].some((s) => tiers[2].has(spellingTiers(s)[2]));
    c.entryIds = entries.map((doc) => doc.word_ID);
    ranked.push(c);
  }
  ranked.sort(
    (a, b) =>
      b.wordListed - a.wordListed ||
      b.listedAsVerb - a.listedAsVerb ||
      a.affixLength - b.affixLength ||
      b.stemListed - a.stemListed ||
      a.guessed - b.guessed ||
      a.order - b.order
  );

  // Several keys can index the same entry (دعو and دعي); offer each entry once.
  const offered = new Set();
  return ranked.filter((c) => {
    if (c.entryIds.every((id) => offered.has(id))) return false;
    c.entryIds.forEach((id) => offered.add(id));
    return true;
  });
};

/**
 * Suggest dictionary roots for a query that wasn't found directly.
 *
 * Primary path: run the query through the aratools morphological analyzer
 * (ported in rootDeriver.js) to detect its actual root letters — so كتاب
 * surfaces كتب, قال surfaces قول, استغفر surfaces غفر, etc. Each derived root
 * is normalized into the same searchable-root key form used for queries and
 * kept only if it exists in the dataset.
 *
 * The analyzer lists every reading in a fixed order that often puts a rare one
 * first (دعا reads as the dual of دعّ before the verb دعا), so roots are ranked:
 *  1. one whose entry lists the word itself, spelled exactly as typed, then up to
 *     hamza seats, then up to ى/ي (أصلي "original" is listed under أصل; folded, it
 *     is also the verb أصلى under صلي);
 *  2. at the same spelling, as a verb rather than a noun (خاف: خافَ under خوف, not
 *     خافٍ "hidden" under خفي);
 *  3. the reading with the fewest affix letters, the article ال counting as one
 *     (واحد itself, not و+أحد; البنك as ال+بنك);
 *  4. one whose entry lists the reading's stem (وزارتها: وزارة);
 *  5. a root the analyzer gave before its guessed و/ي twin; then analyzer order.
 * Measured against independent gold roots (the UD Arabic-PADT treebank: MSA news,
 * 130,000 tokens whose root the dictionary has), the first answer is right for 95.7%
 * of tokens, against 93.6% when the analyzer order was kept.
 *
 * Homographs make this a guess when the top two roots tie on 1 (both list the word,
 * like هام: هامَ "to rove" and هامّ "important", or neither does): that answer is
 * flagged ambiguous. On the same test, suggestions not flagged were right 99.6% of
 * the time; flagged ones (7% of them) 73%.
 *
 * Fallback: if the analyzer surfaces nothing in the dataset and the query is a
 * bare 3-letter root, fall back to the older weak-root variant guesser.
 *
 * @param word          the normalized search key (the route/URL form).
 * @param originalQuery  the user's original spelling (location.state.rawQuery).
 *                       Preferred for analysis because `word` has already had
 *                       its hamza carriers collapsed (أ/ؤ/ئ → ا/و/ا), which
 *                       destroys morphology the analyzer needs.
 * @returns { keys, ambiguous }: the root keys present in the dataset, best first
 *          and one per entry, and whether the first one is a guess among equals.
 */
export const getRootSuggestionsDetailed = async (word, originalQuery) => {
  const normalizedQuery = processInputToArabic((word || "").trim());
  // Letters the map couldn't convert: guessing a root from the rest misleads.
  if (hasUnconvertedLatin(normalizedQuery)) return { keys: [], ambiguous: false };

  const input = rootAnalysisSpelling(originalQuery, word);
  if (!input) return { keys: [], ambiguous: false };

  let ranked = await rankDerivedRoots(input, normalizedQuery);
  // A final ي typed for ى or the reverse (مستشفي, عربى; a Persian keyboard's ی):
  // read it the other way if this way finds nothing.
  if (!ranked.length && /[يى]$/.test(input)) {
    ranked = await rankDerivedRoots(input.slice(0, -1) + (input.endsWith("ي") ? "ى" : "ي"), normalizedQuery);
  }
  if (ranked.length > 0) {
    return {
      keys: ranked.map((c) => c.key),
      ambiguous: ranked.length > 1 && ranked[0].wordListed === ranked[1].wordListed,
    };
  }

  // Nothing from the analyzer — fall back to the legacy weak-root guesser.
  if (normalizedQuery.length === 3) {
    try {
      const keys = await getSarfAlternates(normalizedQuery);
      return { keys, ambiguous: keys.length > 1 };
    } catch (e) {
      console.warn("[getRootSuggestions] getSarfAlternates failed:", e);
    }
  }
  return { keys: [], ambiguous: false };
};

// The suggested root keys only, best first (see getRootSuggestionsDetailed).
export const getRootSuggestions = async (word, originalQuery) =>
  (await getRootSuggestionsDetailed(word, originalQuery)).keys;

// Legacy weak-root guesser: for a bare 3-letter root, propose hollow/defective
// variants (medial/edge alef ↔ و/ي) and keep the ones that exist in the dataset.
export const getSarfAlternates = async (input) => {
  if (input.length !== 3) return [];

  const [f, a, l] = input;
  const candidates = new Set();

  if (a === "ا") {
    candidates.add(f + "و" + l);
    candidates.add(f + "ي" + l);
  } else {
    candidates.add(f + a + "و");
    candidates.add(f + a + "ي");
    candidates.add("و" + a + l);
    candidates.add("ي" + a + l);
  }

  return rootsExistInDataset(Array.from(candidates));
};
